import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Stack, Text, Button, Divider, SimpleGrid } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { SignatureBlock } from './SignatureBlock';
import { SignerEntry } from '../types/template.types';

export interface SignatureSectionProps {
  /** Current list of signers */
  signers: SignerEntry[];
  /** Called when the signer list changes */
  onChange: (signers: SignerEntry[]) => void;
  /** Whether the section is read-only */
  readOnly?: boolean;
}

export const SignatureSection: FC<SignatureSectionProps> = ({
  signers,
  onChange,
  readOnly = false,
}) => {
  const { t } = useTranslation('lims');

  const handleSignerChange = useCallback(
    (updated: SignerEntry) => {
      onChange(signers.map((s) => (s.id === updated.id ? updated : s)));
    },
    [signers, onChange]
  );

  const handleRemove = useCallback(
    (id: string) => {
      onChange(signers.filter((s) => s.id !== id));
    },
    [signers, onChange]
  );

  const handleAdd = useCallback(() => {
    const newSigner: SignerEntry = {
      id: `signer-${Date.now()}`,
      name: '',
      title: '',
    };
    onChange([...signers, newSigner]);
  }, [signers, onChange]);

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Text size="lg" fw={600}>
          {t('reportEditor.signatures.sectionTitle')}
        </Text>

        <Divider />

        <SimpleGrid cols={2} spacing="xl">
          {signers.map((signer) => (
            <SignatureBlock
              key={signer.id}
              signer={signer}
              onChange={handleSignerChange}
              onRemove={() => handleRemove(signer.id)}
              removable={!readOnly && signers.length > 2}
            />
          ))}
        </SimpleGrid>

        {!readOnly && (
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconPlus size={14} />}
            onClick={handleAdd}
            w="fit-content"
          >
            Προσθήκη υπογράφοντα
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

export default SignatureSection;
